import { useRef, useState } from "react";
import { useNotify, useRecordContext } from "react-admin";
import { Box, Button, IconButton, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { customFile } from "features/customFile";
import { documentRequests } from "api/documentsApi";
import { resolveErrorMessage } from "utils/useNotifyError";
import DocumentFileLink from "./DocumentFileLink";

const ACCEPTED_FORMATS = ".png,.jpg,.jpeg,.pdf,.xlsx,.docx,.doc";

// A record's travelDocumentIds may come back either as raw ids or as objects with an id.
// Normalize to a flat array of numeric ids so create/edit always send the shape the BE expects.
export const normalizeTravelDocumentIds = value =>
  Array.isArray(value) ? value.map(item => (item && typeof item === "object" ? item.id : item)).filter(Boolean) : [];

// Multiple travel documents collection. On UPDATE the BE does a full sync to the exact set sent,
// so the parent always submits the complete desired array (sending [] clears all).
const TravelDocumentsInput = ({
  label = "Добави документ за пътуване",
  documentName = "Документ за пътуване",
  value,
  onChange,
}) => {
  const record = useRecordContext();
  const notify = useNotify();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Uncontrolled until the user touches it: fall back to the record's existing docs (edit).
  const ids = value ?? normalizeTravelDocumentIds(record?.travelDocumentIds);

  const handleFiles = async e => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file
    if (!files.length) return;

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const response = await customFile(
          documentRequests.postPrivateDocuments(),
          { File: file, SubCategoryId: 17, name: "DocumentName" },
          "POST",
          null,
          true,
        );
        uploaded.push(response.data);
      }
      onChange([...ids, ...uploaded]);
    } catch (err) {
      notify(resolveErrorMessage(err), { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = id => onChange(ids.filter(current => current !== id));

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FORMATS}
        style={{ display: "none" }}
        onChange={handleFiles}
      />
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? "Качване..." : label}
      </Button>

      <Stack
        spacing={1}
        sx={{ mt: 1 }}
      >
        {ids.map(id => (
          <Box
            key={id}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <DocumentFileLink
              id={id}
              documentName={documentName}
            />
            <IconButton
              size="small"
              aria-label="Премахни"
              onClick={() => handleRemove(id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default TravelDocumentsInput;
